/**
 * useCostEstimate
 *
 * Coverage-aware cost estimates for the home page card. Delegates to the
 * canonical `calculateCost` calculator that the Costs page uses, so the
 * home card and the Costs page can't drift apart. Picks sensible defaults
 * for toggles that the home doesn't yet expose (meds + diagnostics +
 * 1-year storage on, no donor/PGT/ICSI add-ons).
 */

import { useMemo } from "react";
import { useCoverage } from "@src/packages/coverage/hooks/useCoverage";
import type { TreatmentModality } from "@src/packages/experience";
import {
  calculateCost,
  type DiagnosticProfile,
  type TreatmentType,
} from "../../utils/costEstimator";
import type { HomepageGender, HomepageGoal } from "../../homepage.types";

export type CostLineItem = {
  label: string;
  estimatedCostCents: number;
  coveredPercent: number;
  outOfPocketCents: number;
  note?: string;
};

export type CostEstimate = {
  totalEstimatedCents: number;
  totalOutOfPocketCents: number;
  totalCoveredCents: number;
  coverageStatus: "verified" | "pending" | "unverified" | "none";
  lineItems: CostLineItem[];
  reductionStrategies: string[];
};

/**
 * Default reduction strategies. The Costs page surfaces a richer set via
 * the estimator's strategy list; we keep these here as a stable fallback
 * for the home card teaser.
 */
const REDUCTION_STRATEGIES = [
  "Check employer fertility benefits — many tech companies cover $10–50K+",
  "Ask your clinic about multi-cycle discount packages",
  "Look into fertility grants (Baby Quest, The Cade Foundation, etc.)",
  "Some pharmacies offer medication discount programs — ask your clinic",
  "FSA/HSA funds can be used for fertility treatment",
  "Some states mandate fertility coverage — check your state's laws",
];

function mapGoalToTreatmentType(
  goal: HomepageGoal | null,
  gender?: HomepageGender | null,
  modality?: TreatmentModality | null,
): TreatmentType | null {
  if (!goal) return null;

  switch (goal) {
    case "preserve":
      return gender === "male" ? "sperm_freezing" : "egg_freezing";
    case "embryo-preserve":
      return "embryo_freezing";
    case "pregnancy":
      if (modality === "iui") return "iui";
      if (modality === "oi") return "ovulation_induction";
      if (modality !== "ivf") return null;
      return "ivf";
    case "egg-donation":
    case "sperm-donation":
    case "adoption":
    case "surrogacy":
      // No standalone cost estimate for these on the home card.
      return null;
  }
}

export function useCostEstimate(
  goal: HomepageGoal | null,
  gender?: HomepageGender | null,
  modality?: TreatmentModality | null,
): {
  estimate: CostEstimate | null;
  isLoading: boolean;
} {
  const { fertilityCoverage, verificationStatus, isLoading } = useCoverage();

  const estimate = useMemo((): CostEstimate | null => {
    const treatmentType = mapGoalToTreatmentType(goal, gender, modality);
    if (!treatmentType) return null;

    // Home doesn't know partner choice yet — default to single-person
    // profile derived from gender so the diagnostic line items are at
    // least sex-appropriate. Costs page exposes the couple profile when
    // the user has more context.
    const diagnosticProfile: DiagnosticProfile =
      gender === "male" ? "male" : "female";

    const result = calculateCost({
      treatment_type: treatmentType,
      options: {
        donor_sperm: false,
        donor_eggs: false,
        pgt: false,
        sperm_freezing: false,
      },
      toggles: {
        include_medications: true,
        include_diagnostics: true,
        include_one_year_storage: true,
      },
      diagnostic_profile: diagnosticProfile,
    });

    // Coverage layered on top — same simplification the Costs page uses
    // until per-line coverage data is available (flat coinsurance % across
    // all line items).
    const hasCoverage =
      fertilityCoverage?.hasCoverage === true &&
      verificationStatus === "verified";
    const coinsurancePercent = fertilityCoverage?.coinsurancePercent ?? 0;
    const coveredPercent = hasCoverage ? 100 - coinsurancePercent : 0;

    const lineItems: CostLineItem[] = result.breakdown.map((line) => {
      const estCents = Math.round(line.avg * 100);
      const coveredCents = hasCoverage
        ? Math.round(estCents * (coveredPercent / 100))
        : 0;
      return {
        label: line.label,
        estimatedCostCents: estCents,
        coveredPercent: hasCoverage ? coveredPercent : 0,
        outOfPocketCents: estCents - coveredCents,
      };
    });

    const totalEstimatedCents = lineItems.reduce(
      (sum, item) => sum + item.estimatedCostCents,
      0,
    );
    const totalOOPCents = lineItems.reduce(
      (sum, item) => sum + item.outOfPocketCents,
      0,
    );

    return {
      totalEstimatedCents,
      totalOutOfPocketCents: totalOOPCents,
      totalCoveredCents: totalEstimatedCents - totalOOPCents,
      coverageStatus:
        verificationStatus === "unverified" ? "none" : verificationStatus,
      lineItems,
      reductionStrategies: REDUCTION_STRATEGIES,
    };
  }, [fertilityCoverage, verificationStatus, goal, gender, modality]);

  return { estimate, isLoading };
}
